/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { B2CCard, DottedDivider, CustomTheme } from '@masfront/common-b2c'
import { subMonths } from 'date-fns'

import { Box, Link, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import WarningIcon from '@material-ui/icons/Warning'
import { Alert } from '@material-ui/lab'

import { selectAgent } from '~/auth/store/auth.selectors'
import useBalance from '~/balance/hooks/useBalance'
import { useBrand } from '~/brand'
import { Subscription } from '~/customer/model/customer.model'
import useCustomer from '~/customer/useCustomer'
import { makePathname } from '~/net'
import { PATHS } from '~/pages/router/paths'
import { LineItem, CustomCircularProgress } from '~/ui/components'
import { formatFee, formatDate, formatDateToString } from '~/utils'

const useStyles = makeStyles((theme: CustomTheme) => {
  return {
    lineItem: {
      marginBottom: theme.spacing(4),
    },
    button: {
      alignSelf: 'start',
    },
  }
})

interface BalanceCardProps {
  subscriptionNonFixed?: Subscription
  subscriptionFixed?: Subscription
}

interface ParamsProps {
  customerIdentifier: string
  productId: string
}

const BalanceCard: React.FC<BalanceCardProps> = ({ subscriptionNonFixed }) => {
  const subscriptionNonFixedId = subscriptionNonFixed?.id || ''
  const {
    getBalance,
    amount,
    balanceLoading,
    balanceError,
    getRecharges,
    recharges,
    rechargesLoading,
    rechargesError,
  } = useBalance(subscriptionNonFixedId)

  const classes = useStyles()
  const { brand } = useBrand()
  const { agent } = useSelector(selectAgent)
  const history = useHistory()
  const { customerIdentifier, productId }: ParamsProps = useParams()
  const { customer } = useCustomer()
  const provinceId = String(customer && customer.addressInfo.stateId)
  function goToRechargesHistory() {
    history.push({
      pathname: makePathname(PATHS.RECHARGES, { productId, customerIdentifier }),
      search: `?brand=${brand}`,
    })
  }

  const endDate = new Date()
  const startDate = subMonths(endDate, 3)

  useEffect(() => {
    if (!amount && subscriptionNonFixedId !== '') {
      getBalance(subscriptionNonFixedId, provinceId, agent?.sub)
    }
    if (!recharges && subscriptionNonFixedId !== '') {
      getRecharges({
        subscriptionId: subscriptionNonFixedId,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(endDate),
        sub: agent?.sub,
      })
    }
  }, [subscriptionNonFixedId])

  return (
    <B2CCard title="Saldo" dataHookPrefix="balance">
      <Typography variant="subtitle1">Saldo</Typography>
      <Box m={2} />
      {balanceLoading && (
        <Box mt={2} mb={8}>
          <CustomCircularProgress />
        </Box>
      )}
      {!balanceLoading && !balanceError && (
        <LineItem
          dataHook="current-balance"
          className={classes.lineItem}
          primaryText="Saldo actual"
          secondaryText={formatFee(amount?.valueWithTax || 0)}
          fieldsApart
        />
      )}
      {balanceError && (
        <Box width="100%" pb={8} data-hook="empty-bonuses-alert">
          <Alert severity="error" icon={<WarningIcon fontSize="inherit" />}>
            Error al cargar los datos
          </Alert>
        </Box>
      )}

      <DottedDivider />
      <Box m={3} />

      <Typography variant="subtitle1">Últimos movimientos</Typography>

      {!rechargesLoading && recharges && (
        <>
          {recharges?.length ? (
            <Box data-hook="last-movements" my={3}>
              {recharges.slice(0, 3).map((recharge, i) => (
                <LineItem
                  key={recharge.id}
                  dataHook={`movement-${i}`}
                  className={classes.lineItem}
                  primaryText={formatDate(recharge.date, 'dd/MM/yy - HH:mm')}
                  secondaryText={formatFee(recharge.amount)}
                  isHeader
                  fieldsApart
                />
              ))}
            </Box>
          ) : (
            <Box my={4} fontStyle="italic">
              <Typography variant="body2">No existen movimientos recientes</Typography>
            </Box>
          )}
          <Link
            data-hook="history-button"
            component="button"
            className={classes.button}
            onClick={goToRechargesHistory}
          >
            <Typography className="link" variant="body1">
              Ver histórico
            </Typography>
          </Link>
        </>
      )}
      {rechargesLoading && (
        <Box my={4}>
          <CustomCircularProgress />
        </Box>
      )}
      {rechargesError && !recharges && (
        <Box width="100%" py={4}>
          <Alert severity="error" icon={<WarningIcon fontSize="inherit" />}>
            Ha ocurrido un error al cargar los datos.
          </Alert>
        </Box>
      )}
    </B2CCard>
  )
}

export default BalanceCard
